/** @jsx jsx */
import { css, keyframes, jsx } from '@compiled/react';
import { Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

const shimmerKeyframes = keyframes({
	from: {
		backgroundColor: token('color.background.accent.gray.subtle'),
	},
	to: {
		backgroundColor: token('color.background.accent.gray.bolder'),
	},
});

const skeletonStyles = css({
	backgroundColor: token('color.skeleton'),
	animationDuration: '1.5s',
	animationIterationCount: 'infinite',
	animationTimingFunction: 'linear',
	animationDirection: 'alternate',
});

const activeShimmerStyles = css({
	animationName: `${shimmerKeyframes}`,
});

type SkeletonProps = {
	width: string | number;
	height: string | number;
	/**
	 * Defaults to 100px to allow for any reasonable size skeleton having rounded corners.
	 */
	borderRadius?: string | number;
	/**
	 * Enables the shimmering animation.
	 */
	isShimmering?: boolean;
	/**
	 * Applied as a data-attribute. Use this to target groups of skeletons with the same name (e.g. for applying custom styles)
	 * ```
	 * groupName="my-skeleton" -> <div data-my-skeleton>
	 * ```
	 */
	groupName?: string;
	testId?: string;
};

const Skeleton = ({
	width,
	height,
	borderRadius = '100px',
	isShimmering = false,
	groupName,
	testId,
}: SkeletonProps) => {
	const groupDataAttribute = groupName && `data-${groupName}`;

	return (
		<div
			data-testid={testId}
			css={[
				skeletonStyles,
				isShimmering && activeShimmerStyles,
				// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @compiled/no-css-prop-without-css-function
				{ width, height, borderRadius },
			]}
			{...(groupDataAttribute && { [groupDataAttribute]: 'true' })}
		/>
	);
};

export const ToolbarContentSkeleton = () => (
	<Inline xcss={[wrapperStyles]}>
		<Skeleton height="7px" width="130px" isShimmering />
		<Skeleton height="7px" width="130px" isShimmering />
	</Inline>
);

const wrapperStyles = xcss({
	marginLeft: 'space.150',
	marginRight: 'space.250',
	gap: 'space.250',
	flex: '1',
});
